import React from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatDateWithTimezone } from 'ts/common/common';
import ClientPageHeading from 'ts/common/components/ClientPageHeading';
import ClientPageLayout from 'ts/common/components/ClientPageLayout';
import { Button } from 'ts/common/components/gallery';
import {
    formatCurrencyValue,
    getNextInstallment,
    getOverdueBalance,
    getOverdueInstallments,
    getPartialPaymentAmountText,
    isPaymentAvailable
} from 'ts/common/components/invoice/helpers';
import { useTranslate } from 'ts/common/hooks';
import { replaceHtmlEscapeCharacters } from 'ts/common/utils';

interface Props {
    invoice: SpApi.Client.IInvoice;
}

const ClientInvoicePageLayout: React.FC<React.PropsWithChildren<Props>> = ({
    children,
    invoice
}) => {
    const t = useTranslate('clientInvoice');
    const navigate = useNavigate();
    const location = useLocation();

    const getDaysUntilDueDate = (dueDate: string) => {
        return Math.floor((new Date(dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    };

    const getBannerSubtitle = () => {
        const nextInstallment = getNextInstallment(invoice);

        if (!nextInstallment) {
            const overdueInstallments = getOverdueInstallments(invoice);

            if (overdueInstallments.length === 0) {
                return t('paidInFull', {
                    amount: formatCurrencyValue(Number(invoice.grandTotal), invoice.currencyCode)
                });
            }

            return t('paymentIsOverdue', {
                amount: formatCurrencyValue(getOverdueBalance(invoice), invoice.currencyCode)
            });
        }

        const daysUntilDueDate = getDaysUntilDueDate(nextInstallment.dueDate);
        const partialPaymentAmountText = getPartialPaymentAmountText(invoice);
        const nextInstallmentNumber = invoice.installments.indexOf(nextInstallment) + 1;

        if (nextInstallmentNumber === 1 && invoice.invoiceRetainerLabel) {
            if (daysUntilDueDate < 0) {
                return t('retainerIsOverdue', {
                    invoiceRetainerLabel: invoice.invoiceRetainerLabel,
                    amount: partialPaymentAmountText
                });
            }

            if (daysUntilDueDate === 0) {
                return t('retainerIsDueToday', {
                    invoiceRetainerLabel: invoice.invoiceRetainerLabel,
                    amount: partialPaymentAmountText
                });
            }

            if (daysUntilDueDate === 1) {
                return t('retainerIsDueTomorrow', {
                    invoiceRetainerLabel: invoice.invoiceRetainerLabel,
                    amount: partialPaymentAmountText
                });
            }

            return t('retainerIsDueOnDate', {
                invoiceRetainerLabel: invoice.invoiceRetainerLabel,
                amount: partialPaymentAmountText,
                dueDate: formatDateWithTimezone(nextInstallment.dueDate, invoice.brand.timeZone)
            });
        }

        if (daysUntilDueDate < 0) {
            return t(
                invoice.installments.length === 1 ? 'paymentIsOverdue' : 'nextPaymentIsOverdue',
                {
                    amount: partialPaymentAmountText
                }
            );
        }

        if (daysUntilDueDate === 0) {
            return t(
                invoice.installments.length === 1 ? 'paymentIsDueToday' : 'nextPaymentIsDueToday',
                {
                    amount: partialPaymentAmountText
                }
            );
        }

        if (daysUntilDueDate === 1) {
            return t(
                invoice.installments.length === 1
                    ? 'paymentIsDueTomorrow'
                    : 'nextPaymentIsDueTomorrow',
                {
                    amount: partialPaymentAmountText
                }
            );
        }

        return t(
            invoice.installments.length === 1 ? 'paymentIsDueOnDate' : 'nextPaymentIsDueOnDate',
            {
                amount: partialPaymentAmountText,
                dueDate: formatDateWithTimezone(nextInstallment.dueDate, invoice.brand.timeZone)
            }
        );
    };

    const getBanner = () => {
        const bannerTitle = t('greeting', {
            firstName: invoice.contact.firstName
        });

        const subtitle = getBannerSubtitle();

        return (
            <ClientPageHeading
                subtitle={subtitle ? replaceHtmlEscapeCharacters(subtitle) : undefined}
            >
                {bannerTitle}
            </ClientPageHeading>
        );
    };

    const getHeadingRightContentText = () => {
        const nextInstallment = getNextInstallment(invoice);

        if (!nextInstallment) {
            const overdueInstallments = getOverdueInstallments(invoice);

            if (overdueInstallments.length === 0) {
                return undefined;
            }

            return t('amountOverdue', {
                amount: formatCurrencyValue(getOverdueBalance(invoice), invoice.currencyCode)
            });
        }

        const daysUntilDueDate = getDaysUntilDueDate(nextInstallment.dueDate);
        const partialPaymentAmountText = getPartialPaymentAmountText(invoice);

        if (daysUntilDueDate < 0) {
            return t('amountOverdue', {
                amount: partialPaymentAmountText
            });
        }

        if (daysUntilDueDate === 0) {
            return t('amountDueToday', {
                amount: partialPaymentAmountText
            });
        }

        if (daysUntilDueDate === 1) {
            return t('amountDueTomorrow', {
                amount: partialPaymentAmountText
            });
        }

        return t('amountDueInSomeDays', {
            amount: partialPaymentAmountText,
            days: daysUntilDueDate
        });
    };

    const openPaymentPage = () => {
        navigate(`/invoice/${invoice.publicId}/payment`);
    };

    const getHeadingRightContent = () => {
        if (!isPaymentAvailable(invoice)) {
            return undefined;
        }

        const headingRightContentText = getHeadingRightContentText();

        if (!headingRightContentText) {
            return undefined;
        }

        const isLandingPage = !location.pathname.includes('/payment');

        return (
            <Flex alignItems="center" gap="12px">
                <Text fontSize="14px">{headingRightContentText}</Text>
                {isLandingPage && <Button onClick={openPaymentPage} text={t('payInvoice')} />}
            </Flex>
        );
    };

    return (
        <ClientPageLayout
            banner={getBanner()}
            headingRightContent={getHeadingRightContent()}
            headingText={invoice.brandName}
            showLegalLinks
        >
            {children}
        </ClientPageLayout>
    );
};

export default ClientInvoicePageLayout;
